import {QueryClient} from '@tanstack/react-query';

function makeQueryClient() {
    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                // Prevent immediate refetch after server prefetch
                staleTime: 15 * 60 * 1000, // 15 minutes - match our hook settings
                gcTime: 24 * 60 * 60 * 1000, // 24 hours - keep in cache longer for persistence
                refetchOnWindowFocus: false,
                retry: (failureCount, error) => {
                    // Don't retry on 404s
                    if (error instanceof Error && error.message.includes('404')) {
                        return false;
                    }
                    return failureCount < 3;
                },
            },
            mutations: {
                retry: false,
            },
        },
    });

    // No localStorage persistence: with ISR + server-rendered page content,
    // nothing seeds or reads this cache today, so persisting it to localStorage
    // was pure overhead. The QueryClient is kept ready for future client-side
    // data features (mutations / optimistic updates). Error/404 labels no
    // longer depend on this cache — they read a frozen snapshot
    // (error-labels.generated.ts) so they survive a backend outage.
    return queryClient;
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
    if (typeof window === 'undefined') {
        // Always make a new query client on the server
        return makeQueryClient();
    } else {
        // Make a new query client if we don't already have one
        // This is very important, so we don't re-make a new client if React
        // suspends during the initial render. This may not be needed if we
        // have a suspense boundary BELOW the creation of the query client
        browserQueryClient ??= makeQueryClient();
        return browserQueryClient;
    }
}
